/**
 * One shared map, to look at.
 *
 * The same board component the therapist draws on, with `editable` off: the
 * symbols are identical, so what the family reads here is what was drawn in
 * session rather than a simplified picture of it. There is no live channel and
 * no palette — nothing on this page can write.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { getPortalSystemicMap } from "../../application/portalServices";
import { MapBoard } from "../../../systemic_map";
import type { PublicSystemicMap } from "../../../systemic_map";

export default function PortalSystemicMapPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mapId } = useParams<{ mapId: string }>();
    const [map, setMap] = useState<PublicSystemicMap | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!mapId) return;
        let cancelled = false;

        getPortalSystemicMap(mapId)
            .then((data) => {
                if (cancelled) return;
                setMap(data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [mapId]);

    return (
        <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <Button
                    size="sm"
                    variant="ghost"
                    startIcon={<ArrowLeft size={16} aria-hidden="true" />}
                    onClick={() => navigate("/portal/systemic-maps")}
                >
                    {t("portal.systemicMaps.back")}
                </Button>
                <h1 className="truncate text-lg font-semibold text-content-primary">
                    {map?.title ?? t("portal.systemicMaps.title")}
                </h1>
                <p className="rounded-lg bg-surface-sunken px-2 py-1 text-xs font-medium text-content-secondary">
                    {t("portal.systemicMaps.readOnly")}
                </p>
            </div>

            {loading && (
                <p role="status" className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2
                        size={16}
                        aria-hidden="true"
                        className="animate-spin motion-reduce:animate-none"
                    />
                    {t("common.loading")}
                </p>
            )}

            {!loading && (error || !map) && (
                <p role="alert" className="text-sm text-red-600">
                    {t("portal.systemicMaps.loadError")}
                </p>
            )}

            {!loading && !error && map && (
                <div className="h-[calc(100vh-16rem)] min-h-[420px]">
                    <MapBoard
                        state={map.state}
                        saveStatus="idle"
                        onSubmit={() => {}}
                        editable={false}
                    />
                </div>
            )}
        </>
    );
}
